import { useClientPortalAPI } from "@certn/api";
import { components } from "@certn/api/generated-schemas/client-portal";
import {
  ActionListItem,
  AvatarWithName,
  DialogTrigger,
  Divider,
  FormLabelRAC,
  LinkButton,
  Popover,
  PopoverContent,
  SectionMessage,
  SelectFormikField,
  SelectItem,
  Text,
  TextAreaFormikField,
  TextInputFormikField,
} from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { ClientJsonForm } from "../../../components/client-json-form/ClientJsonForm";
import { useUser } from "../../../hooks/useUser";
import { LAST_SELECTED_CLIENT_GROUP_KEY } from "../../../utils/check-utils";
import { getJsonFormPayload } from "../../../utils/getJsonFormPayload";
import { SelectLanguageFormikField } from "../../../components/select-language-formik-field/SelectLanguageFormikField";

interface Props {
  groups: components["schemas"]["CustomerGroupOptionsList"][] | undefined;
  isGroupsLoading: boolean;
  selectedGroup: string | null;
  setSelectedGroup: (group: string | null) => void;
  validateJsonForm: boolean;
}

export function CreateApplyLinkForm({
  groups,
  isGroupsLoading,
  selectedGroup,
  setSelectedGroup,
  validateJsonForm,
}: Props) {
  const { t } = useTranslation();
  const { details: userDetails } = useUser();
  const { data: packages, isLoading: isPackagesLoading } = useClientPortalAPI(
    selectedGroup
      ? `/api/internal/client-portal/packages/ordereable-packages/customer-group/${selectedGroup}/`
      : null
  );
  const [selectedPackageId, setSelectedPackageId] = useState<
    string | number | null
  >(null);
  const selectedPackage = packages?.find((p) => p.id === selectedPackageId);
  const {
    clientPortalShowCredentialsOnCaseDetailsPage,
    clientPortalEnableLanguageSelectField,
  } = useFlags();

  return (
    <div className={css(styles.flexColumn, { gap: "var(--space-5)" })}>
      <Text style="global/heading-medium">
        <Trans>Apply link details</Trans>
      </Text>
      <TextInputFormikField
        name="name"
        label={t("Apply link name")}
        isRequired
        description={t(
          "Used internally to identify the apply link. The applicant will not see this."
        )}
        testID="apply-link-name"
      />
      <div className={styles.flexColumn} data-testid="apply-link-requestor">
        <FormLabelRAC
          isRequired
          info={
            <DialogTrigger>
              <LinkButton
                testID="requestor-info"
                textStyle="form/body-secondary-link"
              >
                <Trans>What's this?</Trans>
              </LinkButton>
              <Popover placement="top right">
                <PopoverContent>
                  {t(
                    "All applicants who use this link will have their cases automatically assigned to the requestor."
                  )}
                </PopoverContent>
              </Popover>
            </DialogTrigger>
          }
        >
          <Trans>Requestor</Trans>
        </FormLabelRAC>
        <ActionListItem
          padding="var(--space-3) 0"
          title={
            <AvatarWithName
              name={userDetails?.auth0_full_name}
              textStyle="global/heading-medium"
              testID="requestor"
            />
          }
        />
      </div>
      <Divider />
      <Text style="global/heading-medium">
        <Trans>Case details</Trans>
      </Text>
      <SelectFormikField
        name="customer_group"
        label={t("Group")}
        isRequired
        isLoading={isGroupsLoading}
        onSelectionChange={(group) => {
          setSelectedGroup(group?.toString() ?? "");
          localStorage.setItem(
            LAST_SELECTED_CLIENT_GROUP_KEY,
            group?.toString() ?? ""
          );
        }}
        testID="apply-link-group"
      >
        {groups?.map((group) => (
          <SelectItem id={group.id} key={group.id}>
            {group.name}
          </SelectItem>
        ))}
      </SelectFormikField>
      <SelectFormikField
        name="package"
        label={t("Package")}
        isRequired
        isLoading={isPackagesLoading}
        isDisabled={!selectedGroup}
        disabledKeys={packages?.flatMap((packageItem) =>
          !packageItem.can_order ? [packageItem.id] : []
        )}
        testID="apply-link-package"
        onSelectionChange={setSelectedPackageId}
      >
        {packages?.map((packageItem) => (
          <SelectItem id={packageItem.id} key={packageItem.id}>
            {packageItem.name}
          </SelectItem>
        ))}
      </SelectFormikField>
      {clientPortalEnableLanguageSelectField && (
        <SelectLanguageFormikField
          description={t(
            "Sets the language for applicant emails, reports, and the portal experience."
          )}
        />
      )}
      {clientPortalShowCredentialsOnCaseDetailsPage &&
        Boolean(selectedPackage?.credentials?.length) && (
          <SectionMessage
            type="info"
            icon="info"
            sectionTitle={t("{{packageName}} payment", {
              packageName: selectedPackage?.name,
            })}
          >
            {t(
              "Payment for credentials collected in this package are only charged for when you choose to reveal the credentials."
            )}
          </SectionMessage>
        )}
      <TextAreaFormikField
        label={t("Additional message")}
        name="additional_message"
        testID="apply-link-additional-message"
        maxLength={1200}
        description={t("This message will be seen by the applicant.")}
      />
      {selectedPackageId && (
        <ClientJsonForm
          key={selectedPackageId}
          isSubmitted={validateJsonForm}
          clientFormPayload={getJsonFormPayload(packages, selectedPackageId)}
          questionsUrl={`/api/internal/client-portal/customer-groups/${selectedGroup}/order-questions/`}
          isApplyLink
        />
      )}
    </div>
  );
}

const styles = {
  flexColumn: css({
    display: "flex",
    flexDirection: "column",
  }),
};
